import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { msg, fmtFechaHora, CARGOS, Cab, Campo, DocumentoLegal, imprimirDocumento, imprimirConsentimiento } from './comun';

/* Acuerdo de confidencialidad: lo firma el profesional de la salud, una vez por empresa (AM MSP 00025-2025).
   La firma digital es la que el propio profesional registró en Configuración y solo la puede aplicar él, desde su usuario.
   Se usa desde Evaluaciones cuando falta el acuerdo. `profesional` = { USUARIO, NOMBRE }: el usuario de Configuración que atiende. */
export default function FirmarAcuerdo({ profesional, empresaId, onVolver, onHecho }) {
  const [empresas, setEmpresas] = useState([]);
  const [empresa, setEmpresa] = useState(empresaId || '');
  const [info, setInfo] = useState(null);
  const [medio, setMedio] = useState('DIGITAL');
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (empresaId) return;
    api.get('/ocupacional/empresas').then(r => setEmpresas(r.data)).catch(e => setErr(msg(e)));
  }, [empresaId]);

  useEffect(() => {
    if (!empresa) { setInfo(null); return; }
    setCargando(true);
    api.get(`/ocupacional/acuerdos?profesional=${encodeURIComponent(profesional.USUARIO)}&empresa=${empresa}`)
      .then(r => { setInfo(r.data); setErr(''); setMedio(r.data.puede_firmar_digital ? 'DIGITAL' : 'PAPEL'); })
      .catch(e => { setInfo(null); setErr(msg(e)); })
      .finally(() => setCargando(false));
  }, [empresa, profesional.USUARIO]);

  const digitalOk = !!(info && info.puede_firmar_digital);
  const puedeFirmar = !!info && !info.vigente && !guardando && (medio === 'PAPEL' || digitalOk);

  async function firmar() {
    setGuardando(true); setErr('');
    try {
      await api.post('/ocupacional/acuerdos', { profesional: profesional.USUARIO, empresa: Number(empresa), medio });
      onHecho();
    } catch (e) { setErr(msg(e)); } finally { setGuardando(false); }
  }

  const nombre = profesional.NOMBRE || profesional.USUARIO;
  const firmante = { nombre, detalle: [info && info.profesional && info.profesional.CARGO ? CARGOS[info.profesional.CARGO] || info.profesional.CARGO : '', info && info.profesional && info.profesional.CEDULA ? 'C.I. ' + info.profesional.CEDULA : ''].filter(Boolean).join(' · ') };
  const archivo = `Acuerdo de confidencialidad - ${nombre}`;
  function imprimir() {
    setErr('');
    if (info && info.vigente) imprimirConsentimiento(api, info.vigente.COD, { firmante, firma_label: info.documento.firma_label, archivo }).catch(e => setErr(msg(e)));
    else if (info) imprimirDocumento({ titulo: info.documento.titulo, parrafos: info.documento.parrafos, firma_label: info.documento.firma_label, firmante, archivo,
      pie: [`${info.documento.version} · para firmar en papel`] });
  }
  return (
    <div className="mo-card fade-up">
      <Cab onVolver={onVolver} titulo="Acuerdo de confidencialidad" sub={nombre}>
        <button className="btn btn-sm" disabled={!info} onClick={imprimir}>🖨 Imprimir / PDF</button>
        <button className="btn btn-primary btn-sm" disabled={!puedeFirmar} onClick={firmar}>{guardando ? 'Guardando…' : '✓ Firmar acuerdo'}</button>
      </Cab>
      {err && <div className="mo-alert">{err}</div>}
      <div className="mo-cuerpo">
        <div className="mo-note">Este acuerdo lo firma el profesional de la salud, una sola vez por empresa. Debe estar firmado <b>antes</b> de atender a cualquier trabajador de esa empresa.</div>
        {!empresaId && (
          <Campo label="Empresa" req>
            <select className="form-control" value={empresa} onChange={e => setEmpresa(e.target.value)}>
              <option value="">— Elija la empresa —</option>
              {empresas.map(e => <option key={e.COD} value={e.COD}>{e.RAZON_SOCIAL}</option>)}
            </select>
          </Campo>
        )}
        {cargando && <div className="spinner" style={{ margin: '0 auto' }} />}
        {info && info.vigente && (
          <div className="mo-ok" style={{ margin: 0 }}>Ya está firmado y vigente desde el {fmtFechaHora(info.vigente.FECHA_FIRMA)} ({info.vigente.MEDIO === 'PAPEL' ? 'en papel' : 'firma digital'}).</div>
        )}
      </div>
      {info && !info.vigente && (
        <>
          <DocumentoLegal documento={info.documento} />
          <div className="mo-cuerpo">
            <div className="mo-lin">
              <label className="mo-chk"><input type="radio" name="medio" checked={medio === 'DIGITAL'} disabled={!digitalOk} onChange={() => setMedio('DIGITAL')} /> Firma digital (la registrada en Configuración)</label>
              <label className="mo-chk"><input type="radio" name="medio" checked={medio === 'PAPEL'} onChange={() => setMedio('PAPEL')} /> Firmado en papel (solo se registra)</label>
            </div>
            {!digitalOk && info.motivo_no_digital && <div className="mo-note">{info.motivo_no_digital}</div>}
            {medio === 'DIGITAL' && digitalOk && <div className="mo-note">Al firmar se aplicará la firma que usted registró en Configuración, junto con la fecha, la hora y la huella del texto.</div>}
            {medio === 'PAPEL' && <div className="mo-note">Pulse «Imprimir / PDF», firme el documento y guárdelo en el archivo físico. Aquí quedará registrado que se firmó en papel, con el texto exacto y su huella.</div>}
          </div>
        </>
      )}
    </div>
  );
}
